<script>
  import getTranslation from '../../../utilities/getTranslation';
  import Button from '../../Button/Button.svelte';
  import { isCustomerEligibleForOffer, hasActiveSubscription } from '../helper';
  import SelectHorizontal from './ProductOptionsComponents/SelectHorizontal.svelte';
  import RadioButton from './ProductOptionsComponents/RadioButton.svelte';

  export let isMostPopular,
    subscription,
    productMetaDataId,
    currentUserSubscriptionId,
    currentSubscription,
    upcomingSubscription,
    showOffer,
    componentInformationKeys,
    sthp,
    productsWithOffers,
    isWelcomeCampaignEnabled,
    urlStyleLocale;

  const externallyManagedPaymentTypes = [
    'GOOGLE_IAS',
    'APPLE_IAS',
    'DOCOMO',
    'MOBILTEK',
    'PARTNER_API',
    'TELIA',
    'IPX',
  ];

  const isFamilyMember = !subscription.currentSubscription.isPayingUser;

  const hasMultipleSeats = subscription.products.every((product) => product.maxUsers > 1);

  const options = subscription.products.map((product) => {
    const standardPrice = getTranslation(product.informationKeys, 'pricing.price');
    return {
      header: getTranslation(product.informationKeys, 'pricing.header'),
      subheader: getTranslation(product.informationKeys, 'pricing.subheader', ''),
      id: product.metadataId,
      badge: getTranslation(product.informationKeys, 'pricing.savings.v2', ''),
      value: showOffer
        ? getTranslation(product.informationKeys, 'pricing.offer.title', standardPrice)
        : standardPrice,
    };
  });

  const canRestartSelectedSubscription = (productId) => {
    return (
      (currentSubscription.metadataId === productId && currentSubscription.canRestart) ||
      (upcomingSubscription.metadataId === productId && upcomingSubscription.canRestart)
    );
  };

  const findOfferCode = (productId) =>
    subscription.products
      .filter((product) => product.metadataId === productId)
      .map((product) => product.informationKeys['pricing.offer.code'])
      .find((product) => product);

  const shouldShowOfferUsingDeprecatedBehaviour = (productId) => {
    const offerCode = findOfferCode(productId);
    return (
      offerCode &&
      isCustomerEligibleForOffer(
        hasActiveSubscription([currentSubscription, upcomingSubscription]),
        subscription.isCustomerInactive
      )
    );
  };

  const productHasOffer = (productId) =>
    productsWithOffers.find((offerProductId) => offerProductId === productId);

  const getIsButtonDisabled = (productId) => {
    const productAlreadyActive =
      (currentUserSubscriptionId === productId && currentSubscription.active) ||
      (upcomingSubscription.metadataId === productId && upcomingSubscription.active);

    const isNotSubPayer = currentSubscription.metadataId && isFamilyMember;

    const isExternallyManagedSub = externallyManagedPaymentTypes.includes(
      currentSubscription.paymentType
    );

    return productAlreadyActive || isNotSubPayer || isExternallyManagedSub;
  };

  const getButtonText = (productId) => {
    const selectedSameAsCurrent = currentSubscription.metadataId === productId;
    const selectedSameAsUpcoming = upcomingSubscription.metadataId === productId;

    const selectedCurrentOrUpcomingSubId = selectedSameAsCurrent || selectedSameAsUpcoming;

    const selectedProduct = subscription.products.find(
      (product) => product.metadataId === productId
    );

    const shouldShowOffer = isWelcomeCampaignEnabled
      ? productHasOffer(productId)
      : shouldShowOfferUsingDeprecatedBehaviour(productId);

    if (shouldShowOffer) {
      return getTranslation(selectedProduct.informationKeys, 'button.offer.start.v2');
    }

    const hadOrHasSubscription =
      subscription.isCustomerInactive || !!currentSubscription.metadataId;
    if (selectedProduct.trialDays > 0 && !hadOrHasSubscription) {
      return getTranslation(selectedProduct.informationKeys, 'button.trial.start.v2');
    }
    if (canRestartSelectedSubscription(productId)) {
      return getTranslation(selectedProduct.informationKeys, 'button.restart.v2');
    }

    if (
      currentSubscription.active ||
      ((currentSubscription.active || upcomingSubscription.active) &&
        !selectedCurrentOrUpcomingSubId) ||
      (upcomingSubscription.active && selectedSameAsUpcoming)
    ) {
      return getTranslation(selectedProduct.informationKeys, 'button.switch.start.v2');
    }

    if (
      subscription.isCustomerInactive ||
      selectedProduct.trialDays === 0 ||
      (selectedSameAsCurrent && !currentSubscription.active && !currentSubscription.canRestart) ||
      !currentSubscription.active
    ) {
      if (selectedProduct.recurring) {
        return getTranslation(selectedProduct.informationKeys, 'button.recurring.start.v2');
      }
      return getTranslation(selectedProduct.informationKeys, 'button.fixed.start.v2');
    }
  };

  const getButtonUrl = (productId) => {
    const prefix =
      urlStyleLocale === 'country'
        ? `${sthp}/${subscription.countryIso}`
        : `${sthp}/${subscription.countryIso}/${subscription.language}`;
    const signUpUrl = `${prefix}/signup/${productId}`;

    const restartUrl = `${sthp}/${subscription.countryIso}/${subscription.language}/restart`;

    if (isWelcomeCampaignEnabled && productHasOffer(productId)) {
      return signUpUrl;
    } else if (!isWelcomeCampaignEnabled && shouldShowOfferUsingDeprecatedBehaviour(productId)) {
      return `${signUpUrl}?campaignCode=${findOfferCode(productId)}`;
    } else if (canRestartSelectedSubscription(productId)) {
      return restartUrl;
    }
    return signUpUrl;
  };

  const gaCtaTracking = (productId) => {
    window.dataLayer.push({
      event: 'genericTrackingEvent',
      eventCategory: 'Subscription Selection Component',
      eventAction: 'Click Sign Up button',
      eventLabel: productId,
    });
  };

  const updateProductIdChange = (e) => {
    productMetaDataId = e.detail;
  };

  const getButtonType = () => {
    const gen3Markets = [
      'se',
      'no',
      'dk',
      'fi',
      'nl',
      'be',
      'pl',
      'is',
      'tr',
      'bg',
      'it',
      'mx',
      'in',
      'de',
    ];

    const isGen3Market = gen3Markets.includes(subscription.countryIso.toLowerCase());
    if (isGen3Market) return 'black';
    return 'primary';
  };
</script>

<style lang="scss">
  @use './styles/sharedStyles' as *;
  @use '../index' as *;

  .card {
    @include generalCardStyle('cta-section');
  }

  .button-select {
    display: flex;
    justify-content: end;
    margin-block-start: -0.3rem;
    padding-block-end: 1.6rem;

    @include mq($until: desktop) {
      margin-block-start: -0.3rem;
    }

    @include mq($from: desktop) {
      height: auto;
    }

    .subscription-start-button {
      margin-block-start: spacing(1);
    }
  }
</style>

<div
  class="card button-select"
  class:most-popular-border={isMostPopular}
  class:most-popular-card-wrapper={isMostPopular}
>
  {#if subscription.products.length > 1}
    <!-- Show for multiple seats -->
    {#if hasMultipleSeats}
      <SelectHorizontal
        {productMetaDataId}
        {options}
        {subscription}
        {componentInformationKeys}
        {currentUserSubscriptionId}
        {currentSubscription}
        {upcomingSubscription}
        on:handleOfferTerms
        on:handleProductIdChange={updateProductIdChange}
      />
    {:else}
      <!-- Show for mutliple hours -->
      <RadioButton
        {productMetaDataId}
        {options}
        {subscription}
        {componentInformationKeys}
        {currentUserSubscriptionId}
        {currentSubscription}
        {upcomingSubscription}
        on:handleOfferTerms
        on:handleProductIdChange={updateProductIdChange}
      />
    {/if}
  {/if}
  <div class:subscription-start-button={subscription.products.length > 1}>
    <Button
      type={canRestartSelectedSubscription(productMetaDataId) ? 'text' : getButtonType()}
      fullwidth={true}
      link={getButtonUrl(productMetaDataId)}
      disabled={getIsButtonDisabled(productMetaDataId)}
      on:click={gaCtaTracking(productMetaDataId)}
    >
      {getButtonText(productMetaDataId)}
    </Button>
  </div>
</div>
